import React, { useState, useEffect } from 'react';
import { History, CheckCircle2, XCircle, Clock, AlertCircle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function RunHistory() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRuns();
    const interval = setInterval(fetchRuns, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchRuns = async () => {
    try {
      const response = await fetch(`${API_URL}/api/runs?limit=50`);
      const data = await response.json();
      setRuns(data);
    } catch (error) {
      console.error('Error fetching runs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (start, end) => {
    if (!start || !end) return 'N/A';
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffMs = endDate - startDate;
    const diffSecs = Math.floor(diffMs / 1000);
    const mins = Math.floor(diffSecs / 60);
    const secs = diffSecs % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return <CheckCircle2 size={16} />;
      case 'failed': return <XCircle size={16} />;
      case 'running': return <RefreshCw size={16} className="animate-spin" />;
      default: return <Clock size={16} />;
    }
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'status-completed';
      case 'failed': return 'status-failed';
      case 'running': return 'status-running';
      default: return 'status-pending';
    }
  };

  if (loading) {
    return <div className="loading-spinner" />;
  }

  return (
    <div data-testid="run-history-page">
      <div className="section-header">
        <div>
          <h2 className="section-title">Run History</h2>
          <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: 14, marginTop: 4 }}>
            View past tender scout runs and their results
          </p>
        </div>
      </div>

      {runs.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <History size={28} />
          </div>
          <div className="empty-state-title">No runs yet</div>
          <div className="empty-state-text">
            Click "Run Now" to start your first tender scout.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {runs.map((run) => (
            <Card key={run._id} data-testid={`run-row-${run._id}`}>
              <CardContent style={{ padding: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                      <span
                        className={`status-badge ${getStatusClass(run.status)}`}
                        data-testid="run-status-badge"
                      >
                        {getStatusIcon(run.status)}
                        {run.status}
                      </span>
                      <span style={{ color: 'hsl(var(--muted-foreground))', fontSize: 14 }}>
                        {formatDate(run.started_at)}
                      </span>
                    </div>

                    {run.status === 'running' && (
                      <div style={{ marginBottom: 12 }}>
                        <Progress value={run.progress || 0} data-testid="run-progress-indicator" />
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 24, marginTop: 12 }}>
                      <div>
                        <div style={{ fontSize: 24, fontWeight: 700, color: 'hsl(var(--primary))' }}>
                          {run.tenders_found || 0}
                        </div>
                        <div style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>
                          Tenders Found
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 24, fontWeight: 700, color: 'hsl(var(--success))' }}>
                          {run.tenders_new || 0}
                        </div>
                        <div style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>
                          New Tenders
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 24, fontWeight: 700 }}>
                          {run.sites_processed || 0}
                        </div>
                        <div style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>
                          Sites Processed
                        </div>
                      </div>
                      {run.ended_at && (
                        <div>
                          <div style={{ fontSize: 24, fontWeight: 700, color: 'hsl(var(--muted-foreground))' }}>
                            {formatDuration(run.started_at, run.ended_at)}
                          </div>
                          <div style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>
                            Duration
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Notes */}
                    {run.notes && run.notes.length > 0 && (
                      <div style={{ marginTop: 16, padding: 12, background: 'hsl(var(--muted))', borderRadius: 8 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'hsl(var(--muted-foreground))' }}>
                          Notes
                        </div>
                        <ul style={{ fontSize: 13, margin: 0, paddingLeft: 20 }}>
                          {run.notes.map((note, idx) => (
                            <li key={idx} style={{ marginBottom: 4 }}>{note}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Errors */}
                    {run.errors && run.errors.length > 0 && (
                      <div style={{
                        marginTop: 16,
                        padding: 12,
                        background: 'hsl(0 84% 95%)',
                        borderRadius: 8,
                        borderLeft: '4px solid hsl(0 84% 60%)'
                      }}>
                        <div style={{
                          fontSize: 12,
                          fontWeight: 600,
                          marginBottom: 8,
                          color: 'hsl(0 84% 50%)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6
                        }}>
                          <AlertCircle size={14} />
                          Errors
                        </div>
                        <ul style={{ fontSize: 13, margin: 0, paddingLeft: 20, color: 'hsl(0 84% 40%)' }}>
                          {run.errors.map((error, idx) => (
                            <li key={idx} style={{ marginBottom: 4 }}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default RunHistory;
